/**
 * Loyalty Points Tooltip Functionality with Floating UI
 *
 * Handles hover-activated tooltips for desktop and click-activated tooltips for mobile
 * Provides responsive behavior based on device capabilities using Floating UI for positioning
 */

(async function () {
	'use strict';

	// Dynamic import Floating UI functions within the IIFE to prevent global scope leakage
	const {computePosition, flip, shift, offset, arrow, autoUpdate} = await import('@floating-ui/dom');

	class LoyaltyPointsTooltip {
		constructor() {
			this.pinnedTooltips = new Set(); // Track pinned tooltips (desktop click-to-pin)
			this.cleanupFunctions = new Map(); // Track cleanup functions for each tooltip
			this.init();
		}

		init() {
			// Early exit if no tooltip triggers are present on the page
			if (!document.querySelector('.agend-loyalty-points-tooltip-trigger')) {
				return;
			}

			// Detect device capabilities
			this.isHoverCapable = this.detectHoverCapability();

			// Wait for DOM to be ready
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.setupTooltips());
			} else {
				this.setupTooltips();
			}
		}

		/**
		 * Detect if the device supports hover interactions
		 * Uses CSS media query matching for accurate detection
		 */
		detectHoverCapability() {
			// Check if device supports hover and has fine pointer (mouse)
			if (window.matchMedia) {
				return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
			}

			// Fallback: assume hover capable if not a touch device
			return !('ontouchstart' in window);
		}

		setupTooltips() {
			// Find all tooltip triggers
			const triggers = document.querySelectorAll('.agend-loyalty-points-tooltip-trigger');

			triggers.forEach(trigger => {
				this.initializeTrigger(trigger);
			});

			// Handle clicks outside tooltips to close them
			document.addEventListener('click', (event) => this.handleOutsideClick(event));

			// Handle ESC key to close tooltips
			document.addEventListener('keydown', (event) => this.handleKeyDown(event));
		}

		initializeTrigger(trigger) {
			const tooltipId = trigger.getAttribute('aria-describedby');

			if (this.isHoverCapable) {
				// Desktop: Add hover functionality
				this.setupHoverEvents(trigger, tooltipId);

				// Desktop click-to-pin functionality
				trigger.addEventListener('click', (event) => {
					event.preventDefault();
					event.stopPropagation();
					this.togglePinnedTooltip(trigger, tooltipId);
				});
			} else {
				// Mobile: Click-only functionality (unchanged)
				trigger.addEventListener('click', (event) => {
					event.preventDefault();
					event.stopPropagation();
					this.toggleTooltip(trigger);
				});
			}

			// Add keyboard event listener for accessibility (all devices)
			trigger.addEventListener('keydown', (event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					event.stopPropagation();
					this.toggleTooltip(trigger);
				}
			});

			// Add cursor pointer style
			trigger.style.cursor = 'pointer';
		}

		/**
		 * Setup hover events for hover-capable devices (desktop)
		 */
		setupHoverEvents(trigger, tooltipId) {
			const tooltip = document.getElementById(tooltipId);
			if (!tooltip) return;

			// Mouse enter trigger
			trigger.addEventListener('mouseenter', () => {
				this.closeAllTooltips();
				this.showTooltip(trigger, tooltip);
			});

			// Mouse leave trigger
			trigger.addEventListener('mouseleave', () => {
				// Only hide if tooltip is not pinned
				if (!this.isPinned(tooltipId)) {
					this.hideTooltip(trigger, tooltip);
				}
			});

			// Mouse enter tooltip (keep visible while hovering tooltip)
			tooltip.addEventListener('mouseenter', () => {
				// Tooltip stays visible while hovering - no action needed
			});

			// Mouse leave tooltip
			tooltip.addEventListener('mouseleave', () => {
				// Only hide if tooltip is not pinned
				if (!this.isPinned(tooltipId)) {
					this.hideTooltip(trigger, tooltip);
				}
			});
		}

		/**
		 * Pinned state management for click-to-pin functionality
		 */
		isPinned(tooltipId) {
			return this.pinnedTooltips.has(tooltipId);
		}

		pinTooltip(tooltipId) {
			this.pinnedTooltips.add(tooltipId);
		}

		unpinTooltip(tooltipId) {
			this.pinnedTooltips.delete(tooltipId);
		}

		/**
		 * Toggle pinned state for desktop click-to-pin functionality
		 */
		togglePinnedTooltip(trigger, tooltipId) {
			const tooltip = document.getElementById(tooltipId);
			if (!tooltip) return;

			const isVisible = tooltip.classList.contains('is-visible');
			const isPinned = this.isPinned(tooltipId);

			if (isVisible && isPinned) {
				// Currently pinned and visible - unpin and hide
				this.unpinTooltip(tooltipId);
				this.hideTooltip(trigger, tooltip);
			} else if (isVisible && !isPinned) {
				// Currently visible from hover - pin it
				this.pinTooltip(tooltipId);
			} else {
				// Not visible - show and pin
				this.closeAllTooltips();
				this.showTooltip(trigger, tooltip);
				this.pinTooltip(tooltipId);
			}
		}

		toggleTooltip(trigger) {
			const tooltipId = trigger.getAttribute('aria-describedby');
			const tooltip = document.getElementById(tooltipId);

			if (!tooltip) return;

			const isVisible = tooltip.classList.contains('is-visible');

			// Close all other tooltips first
			this.closeAllTooltips();

			if (!isVisible) {
				this.showTooltip(trigger, tooltip);
			}
		}

		async showTooltip(trigger, tooltip) {
			// Find arrow element if it exists
			const arrowElement = tooltip.querySelector('.agend-loyalty-points-tooltip-arrow');

			// Configure Floating UI middleware
			const middleware = [
				offset(8), // 8px gap between trigger and tooltip
				flip(), // Flip to opposite side if not enough space
				shift({padding: 8}) // Keep tooltip in viewport with 8px padding
			];

			// Add arrow middleware if arrow element exists
			if (arrowElement) {
				middleware.push(arrow({element: arrowElement}));
			}

			// Position tooltip using Floating UI
			const {x, y, placement, middlewareData} = await computePosition(trigger, tooltip, {
				placement: 'top', // Default placement
				middleware: middleware
			});

			// Apply position
			Object.assign(tooltip.style, {
				left: `${x}px`,
				top: `${y}px`,
				position: 'absolute'
			});

			// Position arrow if it exists
			if (arrowElement && middlewareData.arrow) {
				const {x: arrowX, y: arrowY} = middlewareData.arrow;
				const side = placement.split('-')[0];

				const staticSide = {
					top: 'bottom',
					right: 'left',
					bottom: 'top',
					left: 'right'
				}[side];

				Object.assign(arrowElement.style, {
					left: arrowX != null ? `${arrowX}px` : '',
					top: arrowY != null ? `${arrowY}px` : '',
					right: '',
					bottom: '',
					[staticSide]: '-6px' // Half of arrow size
				});
			}

			// Add visible class
			tooltip.classList.add('is-visible');

			// Update ARIA attributes
			trigger.setAttribute('aria-expanded', 'true');

			// Focus management for accessibility
			tooltip.setAttribute('tabindex', '-1');

			// Set up auto-update for positioning
			const cleanup = autoUpdate(trigger, tooltip, async () => {
				const {x, y, placement, middlewareData} = await computePosition(trigger, tooltip, {
					placement: 'top',
					middleware: middleware
				});

				Object.assign(tooltip.style, {
					left: `${x}px`,
					top: `${y}px`
				});

				// Update arrow position
				if (arrowElement && middlewareData.arrow) {
					const {x: arrowX, y: arrowY} = middlewareData.arrow;
					const side = placement.split('-')[0];

					const staticSide = {
						top: 'bottom',
						right: 'left',
						bottom: 'top',
						left: 'right'
					}[side];

					Object.assign(arrowElement.style, {
						left: arrowX != null ? `${arrowX}px` : '',
						top: arrowY != null ? `${arrowY}px` : '',
						right: '',
						bottom: '',
						[staticSide]: '-6px'
					});
				}
			});

			// Store cleanup function
			this.cleanupFunctions.set(tooltip.id, cleanup);
		}

		hideTooltip(trigger, tooltip) {
			if (!tooltip) return;

			// Cleanup auto-update
			const cleanup = this.cleanupFunctions.get(tooltip.id);
			if (cleanup) {
				cleanup();
				this.cleanupFunctions.delete(tooltip.id);
			}

			// Remove visible class
			tooltip.classList.remove('is-visible');

			// Update ARIA attributes
			trigger.setAttribute('aria-expanded', 'false');

			// Remove tabindex
			tooltip.removeAttribute('tabindex');

			// Clear pinned state when hiding
			const tooltipId = tooltip.id;
			if (this.isPinned(tooltipId)) {
				this.unpinTooltip(tooltipId);
			}

			// Reset positioning styles
			Object.assign(tooltip.style, {
				left: '',
				top: '',
				position: ''
			});

			// Reset arrow position
			const arrowElement = tooltip.querySelector('.agend-loyalty-points-tooltip-arrow');
			if (arrowElement) {
				Object.assign(arrowElement.style, {
					left: '',
					top: '',
					right: '',
					bottom: ''
				});
			}
		}

		closeAllTooltips() {
			const visibleTooltips = document.querySelectorAll('.agend-loyalty-points-tooltip.is-visible');

			visibleTooltips.forEach(tooltip => {
				const trigger = document.querySelector(`[aria-describedby="${tooltip.id}"]`);

				if (trigger) {
					this.hideTooltip(trigger, tooltip);
				}
			});

			// Clear all pinned states when closing all tooltips
			this.pinnedTooltips.clear();
		}

		handleOutsideClick(event) {
			// Check if click is outside tooltip and trigger
			const isTooltipClick = event.target.closest('.agend-loyalty-points-tooltip');
			const isTriggerClick = event.target.closest('.agend-loyalty-points-tooltip-trigger');

			if (!isTooltipClick && !isTriggerClick) {
				this.closeAllTooltips();
			}
		}

		handleKeyDown(event) {
			if (event.key === 'Escape') {
				this.closeAllTooltips();
			}
		}
	}

// Initialize when DOM is ready
	new LoyaltyPointsTooltip();

})();
